import React, { useState } from 'react';
import {
    Terminal, Search, Code, Eye, RefreshCw, ChevronRight,
    ChevronDown, Package, AlertCircle, Play, Command, MessageSquare
} from 'lucide-react';
import useStore from '../store';
import CommandToolbar from './CommandToolbar';
import DoudianWorkspace from './DoudianWorkspace';

const DevToolsPanel = () => {
    const { sendCommand, targetId } = useStore();
    const [activeTab, setActiveTab] = useState('DOUDIAN');
    const [radarData, setRadarData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedObjects, setExpandedObjects] = useState({});
    const [selectedFunction, setSelectedFunction] = useState(null);

    // Console state
    const [consoleInput, setConsoleInput] = useState('');
    const [consoleHistory, setConsoleHistory] = useState([]);

    // Scan global objects
    const scanObjects = () => {
        if (!targetId) return;
        const code = `
            const result = [];
            for (let key in window) {
                try {
                    const val = window[key];
                    const type = typeof val;
                    const item = { name: key, type };
                    if (type === 'function') {
                        item.preview = { source: val.toString().substring(0, 200) };
                    } else if (type === 'object' && val !== null) {
                        item.preview = Object.keys(val).slice(0, 5);
                    }
                    result.push(item);
                } catch (e) {}
            }
            result;
        `;

        sendCommand('EVAL', { code, id: Date.now() }, (response) => {
            if (response && response.result) {
                setRadarData(Array.isArray(response.result) ? response.result : []);
            }
        });
    };

    // Execute console command
    const executeConsole = (e) => {
        e.preventDefault();
        if (!consoleInput.trim() || !targetId) return;

        const entry = {
            type: 'input',
            code: consoleInput,
            timestamp: Date.now()
        };

        sendCommand('EVAL', { code: consoleInput, id: Date.now() }, (response) => {
            setConsoleHistory(prev => [...prev,
                entry,
            {
                type: 'output',
                result: response?.result || response,
                timestamp: Date.now()
            }
            ]);
        });

        setConsoleInput('');
    };

    // View function source
    const viewFunction = (obj) => {
        if (obj.type !== 'function') return;
        setSelectedFunction(obj);
        setActiveTab('FUNCTION');
    };

    // Toggle object expansion
    const toggleExpand = (name) => {
        setExpandedObjects(prev => ({
            ...prev,
            [name]: !prev[name]
        }));
    };

    // Filter radar data
    const filteredData = radarData.filter(obj =>
        obj.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Group by type
    const groupedData = filteredData.reduce((acc, obj) => {
        const type = obj.type || 'unknown';
        if (!acc[type]) acc[type] = [];
        acc[type].push(obj);
        return acc;
    }, {});

    return (
        <div className="flex flex-col h-full bg-gray-900 text-gray-100">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-gray-800">
                <div className="flex items-center gap-2">
                    <Code className="text-blue-400" size={18} />
                    <h2 className="text-sm font-bold">DevTools</h2>
                </div>
                <button
                    onClick={scanObjects}
                    disabled={!targetId}
                    className="p-1.5 hover:bg-gray-800 rounded text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                    title="Refresh Scan"
                >
                    <RefreshCw size={16} />
                </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-800 overflow-x-auto scrollbar-hide">
                {[
                    { id: 'DOUDIAN', label: 'Doudian', icon: MessageSquare },
                    { id: 'COMMANDS', label: 'Commands', icon: Command },
                    { id: 'RADAR', label: 'Object Radar', icon: Package },
                    { id: 'CONSOLE', label: 'Console', icon: Terminal },
                    { id: 'FUNCTION', label: 'Function', icon: Code }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 text-xs font-medium transition-colors whitespace-nowrap ${activeTab === tab.id
                            ? 'bg-gray-800 text-blue-400 border-b-2 border-blue-400'
                            : 'text-gray-400 hover:text-gray-200'
                            }`}
                    >
                        <tab.icon size={14} />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
                {/* DOUDIAN TAB */}
                {activeTab === 'DOUDIAN' && (
                    <DoudianWorkspace />
                )}

                {/* COMMANDS TAB */}
                {activeTab === 'COMMANDS' && (
                    <CommandToolbar />
                )}

                {/* RADAR TAB */}
                {activeTab === 'RADAR' && (
                    <div className="flex flex-col h-full">
                        {/* Search */}
                        <div className="p-3 border-b border-gray-800">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={14} />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search objects and functions..."
                                    className="w-full bg-gray-800 border border-gray-700 rounded-md py-1.5 pl-9 pr-3 text-xs text-gray-200 focus:outline-none focus:border-blue-500"
                                />
                            </div>
                            <div className="mt-2 text-xs text-gray-500">
                                Found {filteredData.length} objects ({groupedData.function?.length || 0} functions)
                            </div>
                        </div>

                        {/* Object List */}
                        <div className="flex-1 overflow-y-auto p-3 space-y-2">
                            {!targetId ? (
                                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                                    <AlertCircle size={32} className="mb-2 opacity-50" />
                                    <p className="text-sm">No target connected</p>
                                </div>
                            ) : radarData.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                                    <Package size={32} className="mb-2 opacity-50" />
                                    <p className="text-sm">Click refresh to scan objects</p>
                                </div>
                            ) : (
                                Object.entries(groupedData).map(([type, objects]) => (
                                    <div key={type} className="space-y-1">
                                        <div className="text-xs font-bold text-gray-400 uppercase tracking-wide px-2 py-1">
                                            {type}s ({objects.length})
                                        </div>
                                        {objects.map((obj, idx) => (
                                            <div
                                                key={idx}
                                                className="bg-gray-800/50 rounded-lg border border-gray-800 overflow-hidden"
                                            >
                                                <div
                                                    className="flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-800"
                                                    onClick={() => toggleExpand(obj.name)}
                                                >
                                                    {expandedObjects[obj.name] ? (
                                                        <ChevronDown size={14} className="text-gray-500" />
                                                    ) : (
                                                        <ChevronRight size={14} className="text-gray-500" />
                                                    )}
                                                    <div className={`p-1 rounded ${obj.type === 'function' ? 'bg-purple-500/20 text-purple-400' :
                                                        obj.type === 'object' ? 'bg-blue-500/20 text-blue-400' :
                                                            'bg-gray-500/20 text-gray-400'
                                                        }`}>
                                                        {obj.type === 'function' ? <Code size={12} /> : <Package size={12} />}
                                                    </div>
                                                    <span className="flex-1 text-sm font-mono text-gray-200">{obj.name}</span>
                                                    {obj.type === 'function' && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                viewFunction(obj);
                                                            }}
                                                            className="p-1 hover:bg-gray-700 rounded text-blue-400"
                                                            title="View Source"
                                                        >
                                                            <Eye size={12} />
                                                        </button>
                                                    )}
                                                </div>

                                                {expandedObjects[obj.name] && obj.preview && (
                                                    <div className="px-2 pb-2 pl-8">
                                                        <pre className="text-xs text-gray-400 font-mono whitespace-pre-wrap break-all bg-gray-900 p-2 rounded">
                                                            {JSON.stringify(obj.preview, null, 2)}
                                                        </pre>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* CONSOLE TAB */}
                {activeTab === 'CONSOLE' && (
                    <div className="flex flex-col h-full">
                        {/* Console Output */}
                        <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-black/20">
                            {consoleHistory.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                                    <Terminal size={32} className="mb-2 opacity-50" />
                                    <p className="text-sm">Execute code to see results</p>
                                </div>
                            ) : (
                                consoleHistory.map((entry, idx) => (
                                    <div key={idx} className={`text-xs font-mono ${entry.type === 'input' ? 'text-blue-400' : 'text-gray-300'
                                        }`}>
                                        {entry.type === 'input' ? (
                                            <div className="flex gap-2">
                                                <span className="text-gray-500">&gt;</span>
                                                <span>{entry.code}</span>
                                            </div>
                                        ) : (
                                            <div className="pl-4 py-1 bg-gray-900/50 rounded">
                                                <pre className="whitespace-pre-wrap break-all">
                                                    {typeof entry.result === 'object'
                                                        ? JSON.stringify(entry.result, null, 2)
                                                        : String(entry.result)}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Console Input */}
                        <div className="p-3 border-t border-gray-800">
                            <form onSubmit={executeConsole} className="flex gap-2">
                                <input
                                    type="text"
                                    value={consoleInput}
                                    onChange={(e) => setConsoleInput(e.target.value)}
                                    placeholder="Execute JavaScript..."
                                    disabled={!targetId}
                                    className="flex-1 bg-gray-800 border border-gray-700 rounded-md py-1.5 px-3 text-xs text-gray-200 font-mono focus:outline-none focus:border-blue-500 disabled:opacity-50"
                                />
                                <button
                                    type="submit"
                                    disabled={!targetId || !consoleInput.trim()}
                                    className="p-1.5 bg-blue-600 hover:bg-blue-700 rounded text-white transition-colors disabled:opacity-50"
                                >
                                    <Play size={14} />
                                </button>
                            </form>
                        </div>
                    </div>
                )}

                {/* FUNCTION TAB */}
                {activeTab === 'FUNCTION' && (
                    <div className="flex flex-col h-full">
                        {!selectedFunction ? (
                            <div className="flex flex-col items-center justify-center h-full text-gray-500">
                                <Code size={32} className="mb-2 opacity-50" />
                                <p className="text-sm">Select a function from Object Radar</p>
                            </div>
                        ) : (
                            <div className="flex flex-col h-full">
                                {/* Function Header */}
                                <div className="p-3 border-b border-gray-800">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Code className="text-purple-400" size={16} />
                                        <h3 className="text-sm font-bold font-mono">{selectedFunction.name}</h3>
                                    </div>
                                </div>

                                {/* Function Source */}
                                <div className="flex-1 overflow-y-auto p-3">
                                    <div className="bg-gray-950 rounded-lg border border-gray-800 p-3">
                                        <div className="text-xs text-gray-500 mb-2">Source Code:</div>
                                        <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap break-all">
                                            {selectedFunction.preview?.source || 'Source code not available'}
                                        </pre>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DevToolsPanel;
